import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { CartProvider } from "@/context/CartContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerEditPage from "@/pages/CustomerEditPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderCreatePage from "@/pages/OrderCreatePage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import ProductsAdminPage from "@/pages/ProductsAdminPage";
import CategoriesPage from "@/pages/CategoriesPage";
import ProductEditPage from "@/pages/ProductEditPage";
import WebsitePage from "@/pages/WebsitePage";
import BannerEditPage from "@/pages/BannerEditPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import HomePage from "@/pages/HomePage";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CatalogPage from "@/pages/CatalogPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import BlogEditPage from "@/pages/BlogEditPage";
import TeamPage from "@/pages/TeamPage";
import ProfilePage from "@/pages/ProfilePage";
import VendorLivePage from "@/pages/VendorLivePage";
import VendorPage from "@/pages/VendorPage";

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
    <span className="text-sm uppercase tracking-[0.2em]">Servitech Xpert</span>
  </div>
);

const StaffRoute = ({ children, adminOnly }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  if (user.role === "sales_rep") return <Navigate to="/vendor" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
};

const VendorRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portal" replace />;
  return children;
};

const ClientRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to={user.role === "sales_rep" ? "/vendor" : "/admin"} replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.role === "client" ? "/portal" : (user.role === "sales_rep" ? "/vendor" : "/admin")} replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/catalogue" element={<CatalogPage />} />
                <Route path="/catalogue/" element={<CatalogPage />} />
                <Route path="/apropos-fr" element={<AboutPage />} />
                <Route path="/apropos-fr/" element={<AboutPage />} />
                <Route path="/contactez-nous" element={<ContactPage />} />
                <Route path="/contactez-nous/" element={<ContactPage />} />
                <Route path="/dernieres-nouvelles" element={<BlogPage />} />
                <Route path="/dernieres-nouvelles/" element={<BlogPage />} />
                <Route path="/dernieres-nouvelles/:slug" element={<BlogPostPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/product/:productId" element={<ProductPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/portal" element={<ClientRoute><ClientPortalPage /></ClientRoute>} />
                <Route path="/vendor" element={<VendorRoute><VendorPage /></VendorRoute>} />
                <Route path="/admin" element={<StaffRoute><DashboardPage /></StaffRoute>} />
                <Route path="/admin/customers" element={<StaffRoute><CustomersPage /></StaffRoute>} />
                <Route path="/admin/customers/:id/edit" element={<StaffRoute><CustomerEditPage /></StaffRoute>} />
                <Route path="/admin/customers/:id" element={<StaffRoute><CustomerDetailPage /></StaffRoute>} />
                <Route path="/admin/orders" element={<StaffRoute><OrdersPage /></StaffRoute>} />
                <Route path="/admin/orders/new" element={<StaffRoute><OrderCreatePage /></StaffRoute>} />
                <Route path="/admin/orders/:id" element={<StaffRoute><OrderDetailPage /></StaffRoute>} />
                <Route path="/admin/products" element={<StaffRoute adminOnly><ProductsAdminPage /></StaffRoute>} />
                <Route path="/admin/categories" element={<StaffRoute adminOnly><CategoriesPage /></StaffRoute>} />
                <Route path="/admin/products/:productId" element={<StaffRoute adminOnly><ProductEditPage /></StaffRoute>} />
                <Route path="/admin/website" element={<StaffRoute adminOnly><WebsitePage /></StaffRoute>} />
                <Route path="/admin/website/blog/new" element={<StaffRoute adminOnly><BlogEditPage /></StaffRoute>} />
                <Route path="/admin/website/blog/:postId" element={<StaffRoute adminOnly><BlogEditPage /></StaffRoute>} />
                <Route path="/admin/website/new" element={<StaffRoute adminOnly><BannerEditPage /></StaffRoute>} />
                <Route path="/admin/website/:bannerId" element={<StaffRoute adminOnly><BannerEditPage /></StaffRoute>} />
                <Route path="/admin/team" element={<StaffRoute adminOnly><TeamPage /></StaffRoute>} />
                <Route path="/admin/vendor-live" element={<StaffRoute adminOnly><VendorLivePage /></StaffRoute>} />
                <Route path="/admin/profile" element={<StaffRoute><ProfilePage /></StaffRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="bottom-right" />
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
